import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Box,
  Typography,
  Divider,
  Alert,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Driver, DriverFormData } from '../types';
import { driverAPI } from '../services/api';

interface Props {
  open: boolean;
  driver: Driver | null;
  onClose: () => void;
  onSaved: (driver: Driver) => void;
}

const AddDriverDialog: React.FC<Props> = ({ open, driver, onClose, onSaved }) => {
  const [formData, setFormData] = useState<DriverFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    licenseNumber: '',
    licenseExpiry: '',
    licenseClass: 'B',
    dateOfBirth: '',
    employeeId: '',
    status: 'active',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    emergencyContact: {
      name: '',
      relationship: '',
      phone: ''
    },
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (driver) {
      setFormData({
        firstName: driver.firstName,
        lastName: driver.lastName,
        email: driver.email,
        phone: driver.phone,
        licenseNumber: driver.licenseNumber,
        licenseExpiry: new Date(driver.licenseExpiry).toISOString().split('T')[0],
        licenseClass: driver.licenseClass,
        dateOfBirth: new Date(driver.dateOfBirth).toISOString().split('T')[0],
        employeeId: driver.employeeId || '',
        status: driver.status,
        address: driver.address || {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: ''
        },
        emergencyContact: driver.emergencyContact || {
          name: '',
          relationship: '',
          phone: ''
        },
        notes: driver.notes || ''
      });
    } else {
      // Reset form for new driver
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        licenseNumber: '',
        licenseExpiry: '',
        licenseClass: 'B',
        dateOfBirth: '',
        employeeId: '',
        status: 'active',
        address: {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: ''
        },
        emergencyContact: {
          name: '',
          relationship: '',
          phone: ''
        },
        notes: ''
      });
    }
    setError('');
  }, [driver, open]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedChange = (parent: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...(prev[parent as keyof DriverFormData] as any),
        [field]: value
      }
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      let savedDriver;
      if (driver) {
        savedDriver = await driverAPI.updateDriver(driver._id, formData);
      } else {
        savedDriver = await driverAPI.createDriver(formData);
      }
      onSaved(savedDriver);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to save driver');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {driver ? 'Edit Driver' : 'Add New Driver'}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Basic Information */}
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
          Basic Information
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              required
              value={formData.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              required
              value={formData.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone"
              required
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Date of Birth"
              type="date"
              required
              InputLabelProps={{ shrink: true }}
              value={formData.dateOfBirth}
              onChange={(e) => handleChange('dateOfBirth', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Employee ID"
              value={formData.employeeId}
              onChange={(e) => handleChange('employeeId', e.target.value)}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* License Information */}
        <Typography variant="subtitle2" gutterBottom>
          License Information
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="License Number"
              required
              value={formData.licenseNumber}
              onChange={(e) => handleChange('licenseNumber', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              label="License Class"
              required
              value={formData.licenseClass}
              onChange={(e) => handleChange('licenseClass', e.target.value)}
            >
              <MenuItem value="A">Class A</MenuItem>
              <MenuItem value="B">Class B</MenuItem>
              <MenuItem value="C">Class C</MenuItem>
              <MenuItem value="D">Class D</MenuItem>
              <MenuItem value="E">Class E</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Expiry Date"
              type="date"
              required
              InputLabelProps={{ shrink: true }}
              value={formData.licenseExpiry}
              onChange={(e) => handleChange('licenseExpiry', e.target.value)}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Emergency Contact */}
        <Typography variant="subtitle2" gutterBottom>
          Emergency Contact
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Contact Name"
              value={formData.emergencyContact?.name}
              onChange={(e) => handleNestedChange('emergencyContact', 'name', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Relationship"
              value={formData.emergencyContact?.relationship}
              onChange={(e) => handleNestedChange('emergencyContact', 'relationship', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Contact Phone"
              value={formData.emergencyContact?.phone}
              onChange={(e) => handleNestedChange('emergencyContact', 'phone', e.target.value)}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Status & Notes */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Status"
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
              <MenuItem value="on_leave">On Leave</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.licenseNumber}
        >
          {loading ? 'Saving...' : driver ? 'Update Driver' : 'Add Driver'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddDriverDialog;